import React, { useEffect, useState, useCallback } from "react";
import { MapPin, Check, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

const TIER_COLOR = { 1: "#A1A1AA", 2: "#00E5FF", 3: "#16A34A", 4: "#FFD700" };

export default function Housing() {
  const { setCharacter } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/housing");
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post("/housing/move", { district_id: id });
      setCharacter(r.character);
      toast.success("You don move! New crib unlocked 🏠");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const payRent = async () => {
    setBusy("rent");
    try {
      const { data: r } = await api.post("/housing/pay-rent");
      setCharacter(r.character);
      toast.success("Rent settled. Landlord dey smile now.");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const list = data.districts.filter((d) => d.city === data.city);
  const dueDate = data.rent_due_at ? new Date(data.rent_due_at) : null;
  const overdue = dueDate && dueDate < new Date();

  return (
    <div className="px-5 pt-6" data-testid="housing-page">
      <span className="label-xs text-slate-500">{data.city} · Districts</span>
      <h1 className="font-display text-3xl mt-1">Where You <span className="text-[#00E5FF]">Dey Stay</span></h1>

      <div className={`mt-4 rounded-xl p-4 border ${overdue ? "bg-[#FF0055]/15 border-[#FF0055]/40" : "bg-white border-slate-200"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-xs text-slate-500">Weekly rent</div>
            <div className="font-display text-xl text-slate-900">{naira(data.current_rent)}</div>
            <div className="text-xs text-slate-500 mt-1">
              {overdue ? "⚠️ OVERDUE — pay sharp sharp" : `Due ${dueDate?.toLocaleDateString()}`}
            </div>
          </div>
          <button data-testid="pay-rent-btn" onClick={payRent} disabled={busy === "rent"}
            className="bg-[#16A34A] text-white font-bold px-5 py-2.5 rounded-xl label-xs hover:bg-[#4dff94] transition-colors disabled:opacity-50">
            Pay Rent
          </button>
        </div>
      </div>

      <div className="mt-5 space-y-3 mb-4">
        {list.map((d) => {
          const current = d.id === data.current;
          return (
            <div key={d.id} data-testid={`district-${d.id}`}
              className={`bg-white border rounded-xl p-4 ${current ? "border-[#16A34A]" : "border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin size={15} style={{ color: TIER_COLOR[d.tier] }} />
                    <span className="font-semibold text-slate-900">{d.name}</span>
                    {current && <span className="label-xs text-[#16A34A] flex items-center gap-1"><HomeIcon size={11} /> Home</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-tight">{d.vibe}</p>
                  <div className="flex items-center gap-3 mt-2 label-xs text-slate-400">
                    <span className="text-[#16A34A]">{naira(d.rent)}/wk</span>
                    <span className="text-[#FF0055]">+{d.clout} clout</span>
                  </div>
                </div>
              </div>
              {!current && (
                <button data-testid={`move-${d.id}`} onClick={() => move(d.id)} disabled={busy === d.id}
                  className="mt-3 w-full py-2.5 rounded-lg label-xs bg-[#00E5FF] text-slate-900 hover:bg-[#5cefff] transition-colors disabled:opacity-50">
                  {busy === d.id ? "Moving…" : `Move here · ${naira(d.rent)}`}
                </button>
              )}
              {current && (
                <div className="mt-3 w-full py-2.5 rounded-lg label-xs bg-black/40 text-[#16A34A] flex items-center justify-center gap-2">
                  <Check size={13} /> Current crib
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
